import React from 'react';
import { Typography, List, ListItem, ListItemText, Divider } from '@mui/material';
import HelpSidebar from './HelpSidebar';
import AskAardvarkHelpHint from './AskAardvarkHelpHint';

interface OrganisationWorkforceHelpSidebarProps {
  isExpanded: boolean;
  onToggle: () => void;
  scope?: 'organisation' | 'team';
}

export const OrganisationWorkforceHelpSidebar: React.FC<OrganisationWorkforceHelpSidebarProps> = ({
  isExpanded,
  onToggle,
  scope = 'organisation',
}) => (
  <HelpSidebar isExpanded={isExpanded} onToggle={onToggle} title="Organisation Workforce Help">
    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
      {scope === 'team' ? (
        <>
          Figures are a point-in-time snapshot for your selected workforce scope, calculated when you load or refresh this page.
          Team scope follows your reporting tree. Comparison values (for example company mean tenure) are benchmarked against{' '}
          <strong>all active users</strong> in the organisation.
        </>
      ) : (
        <>
          Figures are a point-in-time snapshot for your selected workforce scope. In the <strong>All</strong> view, this
          includes <strong>all active users</strong> in the organisation.
        </>
      )}{' '}
      Date/time values with a clock time follow Times on screen on your Profile (organisation time zone by default). Calendar dates stay the same for everyone.
    </Typography>

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      TENURE (MEDIAN, MEAN &amp; CHART)
    </Typography>
    <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
      <strong>Who counts:</strong> Only people with a <strong>date of engagement</strong> on or before the as-at date, and that date is not in the future. People without an engagement date are excluded from median, mean, and the tenure distribution chart (they still appear in headcount and in the &quot;Missing engagement date&quot; tile).
    </Typography>
    <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
      <strong>Approximate years:</strong> For each included person, tenure in years is{' '}
      <strong>(as-at date − engagement date) in days ÷ 365.25</strong>. That is a smooth year length (not calendar years), so it aligns with the decimal values shown for median and mean.
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Median tenure"
          secondary="All those approximate-year values are sorted. If there is an odd number of people, the median is the middle value. If there is an even number, it is the average of the two middle values. Shown to one decimal place."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Mean tenure"
          secondary="The arithmetic average of the same approximate-year values (sum ÷ count). Shown to two decimal places."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Tenure distribution chart"
          secondary="Each person is placed in exactly one band using the same approximate years: under 1 year; 1–3; 3–5; 5–10; 10+. The upper end is exclusive except the top band (e.g. 3 years goes to the 3–5 band, not 1–3)."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Length of service (table)"
          secondary="The table shows a readable years/months string derived from calendar difference (not the 365.25 rule). Export to Excel includes both that display and the approximate decimal years for analysis."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      CELEBRATIONS &amp; JOINERS
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Birthdays this month"
          secondary="Uses month and day of date of birth; the calendar day in the current year must fall in the same month as the as-at date. Leap-day birthdays use 28 February in non-leap years."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Work anniversaries this month"
          secondary="Same idea for date of engagement: the anniversary in the current calendar year that falls in the as-at month."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Milestone anniversaries (this quarter)"
          secondary="Calendar quarters (Q1–Q4). Listed when the work anniversary in the current year falls in the current quarter and completed full years of service are 5, 10, 15, and so on (multiples of five, at least five)."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="New joiners (90 days)"
          secondary="Count of active users whose date of engagement is within the last 90 days up to and including the as-at date. The same section now lists the names and join dates in a dedicated New joiners column."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      ATTRITION (CALENDAR YEAR)
    </Typography>
    <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
      Board-style attrition for the last five calendar years (current year is year-to-date). Uses{' '}
      <strong>date of exit</strong> (last day of employment) and <strong>date of engagement</strong>. Anyone without an
      engagement date is excluded from attrition maths (they already appear under profile completeness).
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Leavers"
          secondary="People whose date of exit falls in that calendar year (within the selected workforce scope)."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Opening / closing headcount"
          secondary="Employed on 1 January and on 31 December of that year (or today for the current year). Someone is employed on a date when engagement is on or before that date and there is no exit date, or the exit date is on or after that date."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Attrition rate"
          secondary="Leavers ÷ average headcount × 100, where average headcount is (opening + closing) ÷ 2. Shown as a percentage to one decimal place."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      DATA QUALITY &amp; TABLE
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Missing date of birth / engagement / exit / company / role template"
          secondary="Counts of people with incomplete profile data. Birth, engagement, company, and role template apply to active users. Date of exit applies to inactive users (assumed leavers) who still need an exit date for attrition. Clicking an orange card jumps to Employees and filters to those people."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Next anniversary"
          secondary="Next work anniversary from the as-at date: this year’s anniversary if it is still to come or today; otherwise next year’s."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>

    <Divider sx={{ my: 2 }} />

    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
      EMPLOYEES TABLE &amp; EXPORT
    </Typography>
    <List dense sx={{ mb: 2 }}>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Search and filters"
          secondary={
            <>
              Search matches <strong>name and email</strong> only. Use the <strong>Department</strong> and{' '}
              <strong>Business unit</strong> dropdowns to narrow by those fields. A temporary profile-completeness filter
              appears when launched from the orange cards.
            </>
          }
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Sortable columns"
          secondary="Click a column header to sort (e.g. business unit, next anniversary). Long text in cells may show an ellipsis; hover for the full value where a tooltip is provided."
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
      <ListItem sx={{ px: 0, py: 0.5 }}>
        <ListItemText
          primary="Export to Excel"
          secondary={
            <>
              Available to <strong>managers and administrators only</strong>. Downloads an <strong>.xlsx</strong>{' '}
              workbook (one <strong>Workforce</strong> sheet) for the rows currently shown in the table—after search and
              dropdown filters—with the same columns as on screen plus approximate tenure in years for analysis.
            </>
          }
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
          secondaryTypographyProps={{ variant: 'body2' }}
        />
      </ListItem>
    </List>
    <AskAardvarkHelpHint about="this workforce snapshot — headcount, tenure, attrition, or who has a birthday this month" />
  </HelpSidebar>
);

export default OrganisationWorkforceHelpSidebar;
